import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CheckIcon, Loader2Icon, MailCheckIcon } from 'lucide-react';
import { AuthLayout } from '../../layouts/AuthLayout';
import { useAuth } from '../../contexts/AuthContext';
import { updateOrganization } from '../../services/organizationService';
import { SECTOR_OPTIONS } from '../../data/sectors';
import { BrandLogo } from '../../components/shared/BrandLogo';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeatPassword: string;
  companyName: string;
  sector: string;
  primaryColor: string;
  secondaryColor: string;
}
const INITIAL: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  repeatPassword: '',
  companyName: '',
  sector: '',
  primaryColor: '#3D8BFF',
  secondaryColor: '#0B1F4D'
};
// El registro es lo más breve posible: el branding (colores, tipografías) se
// configura después desde Configuración → Identidad de empresa.
const STEPS = ['Tu cuenta', 'Tu empresa', 'Logotipo', 'Sector'];
const CREATING_STEPS = ['Creando tu cuenta', 'Creando el espacio de trabajo de tu empresa', 'Preparando los estados iniciales del pipeline'];
export function Register() {
  const {
    register,
    loading
  } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState<FormState>(INITIAL);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  /** La cuenta se ha creado pero necesita confirmarse por email. */
  const [confirmEmail, setConfirmEmail] = useState(false);
  function set(key: keyof FormState, value: string) {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: ''
    }));
  }
  function validateStep(index: number): boolean {
    const next: Record<string, string> = {};
    if (index === 0) {
      if (!values.firstName.trim()) next.firstName = 'Introduce tu nombre.';
      if (!values.lastName.trim()) next.lastName = 'Introduce tus apellidos.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = 'Introduce un email válido.';
      if (values.password.length < 8) next.password = 'Usa al menos 8 caracteres.';
      if (values.password !== values.repeatPassword) next.repeatPassword = 'Las contraseñas no coinciden.';
      if (!terms) next.terms = 'Debes aceptar los términos.';
    }
    if (index === 1 && !values.companyName.trim()) next.companyName = 'Introduce el nombre de tu empresa o tu nombre.';
    if (index === 3 && !values.sector) next.sector = 'Selecciona un sector.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  async function finish() {
    if (!validateStep(3)) return;
    setCreating(true);
    try {
      const session = await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        companyName: values.companyName,
        sector: values.sector,
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        logoUrl: logo
      });

      // Sin sesión: la cuenta debe confirmarse por email y el espacio de
      // trabajo se creará en el primer acceso.
      if (!session) {
        setCreating(false);
        setConfirmEmail(true);
        return;
      }
      await updateOrganization(session.organizationId, {
        logoUrl: logo,
        brand: {
          primaryColor: values.primaryColor,
          secondaryColor: values.secondaryColor,
          fontFamily: 'Inter',
          logoUrl: logo
        }
      });
      navigate('/app/dashboard');
    } catch (err) {
      setCreating(false);
      setStep(0);
      setErrors({
        email: err instanceof Error ? err.message : 'No se ha podido crear la cuenta.'
      });
    }
  }
  if (confirmEmail) {
    return <AuthLayout title="Confirma tu correo" subtitle={`Hemos enviado un enlace de confirmación a ${values.email}.`} footer={<span className="text-muted-foreground" data-id="element-1677" data-mp="17hskpqklssiz">
            ¿Ya lo has confirmado?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1678" data-mp="1met2b192z73q">
              Iniciar sesión
            </Link>
          </span>} data-id="element-1676" data-mp="1cfu3eae4aptx">
        <div className="space-y-5 text-sm leading-relaxed text-muted-foreground" data-id="element-1679" data-mp="3o6ei5cnm2a8c">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10" data-id="element-1680" data-mp="3o8iumizkshk8">
            <MailCheckIcon className="h-6 w-6 text-primary" aria-hidden="true" data-id="element-1681" data-mp="3j9xvynzz25xw" />
          </span>
          <p data-id="element-1682" data-mp="1yrwtfsdmtap8">
            Abre el enlace del correo para activar tu cuenta. Al iniciar sesión
            por primera vez crearemos automáticamente el espacio de trabajo de{' '}
            <strong className="text-foreground" data-id="element-1683" data-mp="1ozhbgv2c4m4c">{values.companyName}</strong> con
            tu logotipo, tus colores, los estados del embudo y tu primera landing.
          </p>
          <p data-id="element-1684" data-mp="1fy9p9bd3fbqm">
            Si no lo encuentras, revisa la carpeta de spam. El enlace caduca en
            24 horas.
          </p>
          <Link to="/login" data-id="element-1685" data-mp="1jyjv8o0vy25h">
            <Button size="lg" className="w-full" data-id="element-1686" data-mp="1615155zot676">
              Ir a iniciar sesión
            </Button>
          </Link>
        </div>
      </AuthLayout>;
  }
  if (creating) {
    return <AuthLayout title="Estamos creando tu CRM" subtitle={`Espacio de trabajo de ${values.companyName}`} data-id="element-1687" data-mp="1b1sz6bmc2naw">
        <ul className="space-y-4" data-id="element-1688" data-mp="35dzarbj5363q">
          {CREATING_STEPS.map((label, index) => <li key={label} className="flex items-center gap-3 text-sm" data-id="element-1689" data-mp="35bwp13p0rz4t" data-mpi={index}>
              {index === 0 ? <CheckCircle2Icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-1690" data-mp="33y52m584mxge" /> : <Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" data-id="element-1691" data-mp="2ywpfm7me2gvd" />}
              <span className="text-foreground" data-id="element-1692" data-mp="2tzfis6wo72xi">{label}</span>
            </li>)}
        </ul>
      </AuthLayout>;
  }
  const stepper = <ol className="mb-9 flex items-center gap-2" aria-label="Progreso del registro" data-id="element-1693" data-mp="2p1dzplz8v3m1">
      {STEPS.map((label, index) => <li key={label} className="flex flex-1 items-center gap-2" data-id="element-1694" data-mp="3mp600otlmkxu" data-mpi={index}>
          <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-[11px] font-semibold transition-colors ${index < step ? 'bg-primary/10 text-primary' : index === step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`} data-id="element-1695" data-mp="3hoqom2svnnii">
            {index < step ? <CheckIcon className="h-3.5 w-3.5" data-id="element-1696" data-mp="996y3bjlcj75" /> : index + 1}
          </span>
          {index < STEPS.length - 1 && <span className={`h-px flex-1 ${index < step ? 'bg-primary/40' : 'bg-border'}`} aria-hidden="true" data-id="element-1697" data-mp="4g5i107nedlz" />}
        </li>)}
    </ol>;
  return <AuthLayout title={step === 0 ? 'Crea el CRM de tu empresa' : `Paso ${step + 1} · ${STEPS[step]}`} subtitle={step === 0 ? 'Tu espacio de trabajo se crea automáticamente y solo tu equipo podrá verlo.' : 'Personaliza el espacio de trabajo de tu empresa. Podrás cambiarlo cuando quieras.'} footer={step === 0 ? <span className="text-muted-foreground" data-id="element-1699" data-mp="vpgooecnwugf">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1700" data-mp="zqsi6uynpztw">
              Iniciar sesión
            </Link>
          </span> : undefined} data-id="element-1698" data-mp="gry4lpaxj5fb">
      {stepper}

      <div className="space-y-5" data-id="element-1701" data-mp="14iah7dh4dqc8">
        {step === 0 && <>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1702" data-mp="19fk4amh1dpww">
              <Input id="firstName" label="Nombre" value={values.firstName} error={errors.firstName} onChange={e => set('firstName', e.target.value)} data-id="element-1703" data-mp="1efooamh5l2rv" />
              <Input id="lastName" label="Apellidos" value={values.lastName} error={errors.lastName} onChange={e => set('lastName', e.target.value)} data-id="element-1704" data-mp="1jfku8r9ev4a2" />
            </div>
            <Input id="email" label="Email" type="email" autoComplete="email" value={values.email} error={errors.email} onChange={e => set('email', e.target.value)} data-id="element-1705" data-mp="1zr6dr37xkv6b" />
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1706" data-mp="2do21m1sucr6f">
              <Input id="password" label="Contraseña" type="password" autoComplete="new-password" value={values.password} error={errors.password} onChange={e => set('password', e.target.value)} data-id="element-1707" data-mp="28se9bdj2bhwp" />
              <Input id="repeatPassword" label="Repetir contraseña" type="password" autoComplete="new-password" value={values.repeatPassword} error={errors.repeatPassword} onChange={e => set('repeatPassword', e.target.value)} data-id="element-1708" data-mp="1h16skrf7o086" />
            </div>
            <div data-id="element-1709" data-mp="1pz0oi192crut">
              <Checkbox id="terms" label="Acepto los términos y la política de privacidad." checked={terms} onChange={e => {
            setTerms(e.target.checked);
            setErrors(prev => ({
              ...prev,
              terms: ''
            }));
          }} data-id="element-1710" data-mp="12azjv3f09vko" />
              {errors.terms && <p role="alert" className="mt-2 text-sm font-medium text-red-600" data-id="element-1711" data-mp="3dq86vqsti5ho">
                  {errors.terms}
                </p>}
            </div>
          </>}

        {step === 1 && <Input id="companyName" label="Nombre de empresa o persona" placeholder="Grupo Empresarial XYZ o Carlos Martínez" value={values.companyName} error={errors.companyName} onChange={e => set('companyName', e.target.value)} data-id="element-1712" data-mp="2zwqpocnlt4ut" />}

        {step === 2 && <>
            <LogoUploader value={logo} onChange={setLogo} data-id="element-1713" data-mp="iojjfyrcody" />
            <div className="rounded-xl border border-border bg-surface p-4" data-id="element-1714" data-mp="3ipl4n6l88wz1">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-1715" data-mp="3irfi5zu65mt3">
                Vista previa
              </p>
              <div className="flex items-center gap-3 rounded-lg bg-background px-4 py-3" data-id="element-1716" data-mp="279wds44urdsy">
                <BrandLogo src={logo} size={22} data-id="element-1717" data-mp="2c9itqlc2f85g" />
                <span className="text-sm text-muted-foreground" data-id="element-1718" data-mp="2c761nfclfheo">
                  {values.companyName || 'Tu empresa'}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-id="element-1719" data-mp="2rxxki851xj72">
              Puedes continuar sin logotipo y añadirlo más adelante desde
              Configuración → Identidad de empresa.
            </p>
          </>}

        {step === 3 && <>
            <Select id="sector" label="Sector de la empresa" placeholder="Selecciona un sector" options={SECTOR_OPTIONS} value={values.sector} error={errors.sector} onChange={e => set('sector', e.target.value)} data-id="element-1720" data-mp="248w7e7l9xjvg" />
            <p className="text-xs text-muted-foreground" data-id="element-1721" data-mp="1ub8b3d80s4b6">
              Los colores y la tipografía de tu marca se configuran después
              desde Configuración → Identidad de empresa.
            </p>
          </>}
      </div>

      <div className="mt-9 flex items-center justify-between gap-3" data-id="element-1722" data-mp="3oq16bb4zqt2l">
        <Button variant="ghost" disabled={step === 0} leftIcon={<ArrowLeftIcon className="h-4 w-4" data-id="element-1724" data-mp="3tn199d6jnfph" />} onClick={() => setStep(s => Math.max(0, s - 1))} data-id="element-1723" data-mp="3tocfrkrdj1ka">
          Anterior
        </Button>
        {step < STEPS.length - 1 ? <Button size="lg" rightIcon={<ArrowRightIcon className="h-4 w-4" data-id="element-1726" data-mp="1d2xk5pdblg61" />} onClick={() => {
        if (validateStep(step)) setStep(s => s + 1);
      }} data-id="element-1725" data-mp="1d2fhob02e5po">
            Continuar
          </Button> : <Button size="lg" className="ava-glow" loading={loading} onClick={finish} data-id="element-1727" data-mp="1mzadun8v7u0s">
            Crear mi CRM
          </Button>}
      </div>
    </AuthLayout>;
}